import { useEffect, useMemo, type ReactNode } from 'react';

import { useMultiSiteConnections } from '@/dataAccess/realTime';
import { stopAllConnections } from '@/dataAccess/realTime/connection/useMultiSiteConnections/useMultiSiteConnections.functions.ts';

import { SignalRContext } from './hubProvider/hubProviderContext.ts';

type HubProviderProps = {
  children: ReactNode;
};

export const HubProvider = ({ children, ...rest }: HubProviderProps) => {
  const { connections, isLoading, pendingSites, refetchDisconnected } = useMultiSiteConnections();

  // Reconnect sites whose connection has fully dropped (after SignalR's own
  // retries gave up). No-op while a reconnect is already in flight.
  useEffect(() => {
    refetchDisconnected();
  }, [/* effect dep */ connections, refetchDisconnected]);

  // Stop every live connection when the provider unmounts. Must not run on
  // re-render: connections arrive one site at a time, and stopping fresh
  // connections whenever the map changes would kill them.
  useEffect(() => stopAllConnections, []);

  const value = useMemo(() => {
    return { connections, isLoading, pendingSites };
  }, [connections, isLoading, pendingSites]);

  return (
    <SignalRContext value={value} {...rest}>
      {children}
    </SignalRContext>
  );
};
